'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { SvgManual } from '@common/icon';
import { cn } from '@core/utils';
import type { DetailHomeInstitutionResponse, ListHomeGiftProductByInstitutionResponse } from '@generated/unauth';
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { CartIconLink } from '@/app/donation/_components/CartIconLink';
import { PageNavigation } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useEventListTotalCount } from '../_hooks/useEventListTotalCount';
import { HOMETOWNS_DETAIL_TAB } from '../constants';
import { AppBack } from './AppBack';
import { BenefitsContent } from './BenefitsContent';
import { ContentsContent } from './ContentsContent';
import { GiftsContent } from './GiftsContent';
import { HometownsTab } from './HometownsTab';
import { MainInfoSection } from './MainInfoSection';
import { PolicyContent } from './PolicyContent';
import { ProjectsContent } from './ProjectsContent';
import { ReviewsContent } from './ReviewsContent';
const SectionSpinner = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="py-40 flex-center"><Spinner /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface HometownsDetailTemplateProps {
  institutionId: number;
  institutionData: DetailHomeInstitutionResponse;
  giftProductData?: ListHomeGiftProductByInstitutionResponse;
}
export const HometownsDetailTemplate = t0 => {
  const $ = _c(63);
  const {
    institutionId,
    institutionData,
    giftProductData
  } = t0;
  let t1;
  if ($[0] !== institutionId) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/home/donation-business"]({
      query: {
        institutionId,
        page: 1,
        size: 1
      }
    });
    $[0] = institutionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      select: _temp
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: projectsCount
  } = useQuery(t2);
  let t3;
  if ($[4] !== institutionId) {
    t3 = UnauthQueryOptions["get:/wello/v2/allows/home/institution-content"]({
      query: {
        institutionId,
        page: 1,
        size: 1
      }
    });
    $[4] = institutionId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      ...t3,
      select: _temp2
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: contentsCount
  } = useQuery(t4);
  let t5;
  if ($[8] !== institutionId) {
    t5 = UnauthQueryOptions["get:/wello/v2/allows/home/institution/policy"]({
      query: {
        institutionId,
        page: 1,
        size: 1
      }
    });
    $[8] = institutionId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      ...t5,
      select: _temp3
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    data: policyCount
  } = useQuery(t6);
  let t7;
  if ($[12] !== institutionId) {
    t7 = UnauthQueryOptions["get:/wello/v2/allows/home/gift-review"]({
      query: {
        institutionId,
        page: 1,
        size: 1
      }
    });
    $[12] = institutionId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = {
      ...t7,
      select: _temp4
    };
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: reviewsCount
  } = useQuery(t8);
  const {
    data: benefitsCount
  } = useEventListTotalCount(institutionId);
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AppBack />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CartIconLink />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <PageNavigation donationHomeButton={true} isNotUsedHomeButton={true}>{t10}<CustomLink href={DONATION_ROUTES.GUIDE.pathname} aria-label={"\uAC00\uC774\uB4DC \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"}><SvgManual className="size-24 text-Gray-900" /></CustomLink></PageNavigation>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== institutionData.backgroundVideoImageUrl || $[20] !== institutionData.description || $[21] !== institutionData.institutionName || $[22] !== institutionData.logoUrl || $[23] !== institutionData.pcBackgroundVideoImageUrl || $[24] !== institutionData.summary || $[25] !== institutionId) {
    t12 = <MainInfoSection title={institutionData.institutionName} summary={institutionData.summary} description={institutionData.description} institutionId={institutionId} backgroundVideoImageUrl={institutionData.backgroundVideoImageUrl} pcBackgroundVideoImageUrl={institutionData.pcBackgroundVideoImageUrl} logoUrl={institutionData.logoUrl} />;
    $[19] = institutionData.backgroundVideoImageUrl;
    $[20] = institutionData.description;
    $[21] = institutionData.institutionName;
    $[22] = institutionData.logoUrl;
    $[23] = institutionData.pcBackgroundVideoImageUrl;
    $[24] = institutionData.summary;
    $[25] = institutionId;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("sticky top-0 z-10 mt-40 bg-transparent px-40 content-container", "mobile-tablet:mt-24 mobile-tablet:px-0");
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== benefitsCount || $[29] !== contentsCount || $[30] !== policyCount || $[31] !== projectsCount || $[32] !== reviewsCount) {
    t14 = filterTabs({
      projectsCount,
      contentsCount,
      policyCount,
      reviewsCount,
      benefitsCount
    });
    $[28] = benefitsCount;
    $[29] = contentsCount;
    $[30] = policyCount;
    $[31] = projectsCount;
    $[32] = reviewsCount;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <header className={t13}><HometownsTab tabs={t14} /></header>;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== giftProductData || $[37] !== institutionId) {
    t16 = giftProductData && giftProductData.totalCount > 0 ? <section id={HOMETOWNS_DETAIL_TAB.GIFTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ErrorBoundary fallback={null}><Suspense fallback={<SectionSpinner />}><GiftsContent institutionId={institutionId} initialData={giftProductData.contents} /></Suspense></ErrorBoundary></section> : null;
    $[36] = giftProductData;
    $[37] = institutionId;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== institutionId || $[40] !== projectsCount) {
    t17 = projectsCount !== 0 ? <section id={HOMETOWNS_DETAIL_TAB.PROJECTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ProjectsContent institutionId={institutionId} /></section> : null;
    $[39] = institutionId;
    $[40] = projectsCount;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== contentsCount || $[43] !== institutionId) {
    t18 = contentsCount !== 0 ? <section id={HOMETOWNS_DETAIL_TAB.CONTENTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ContentsContent institutionId={institutionId} /></section> : null;
    $[42] = contentsCount;
    $[43] = institutionId;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== institutionId || $[46] !== policyCount) {
    t19 = policyCount !== 0 ? <section id={HOMETOWNS_DETAIL_TAB.POLICY} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><PolicyContent institutionId={institutionId} /></section> : null;
    $[45] = institutionId;
    $[46] = policyCount;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== institutionId || $[49] !== reviewsCount) {
    t20 = reviewsCount !== 0 ? <section id={HOMETOWNS_DETAIL_TAB.REVIEWS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ErrorBoundary fallback={null}><Suspense fallback={<SectionSpinner />}><ReviewsContent institutionId={institutionId} /></Suspense></ErrorBoundary></section> : null;
    $[48] = institutionId;
    $[49] = reviewsCount;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== benefitsCount || $[52] !== institutionId) {
    t21 = benefitsCount !== 0 ? <section id={HOMETOWNS_DETAIL_TAB.BENEFITS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><BenefitsContent institutionId={institutionId} /></section> : null;
    $[51] = benefitsCount;
    $[52] = institutionId;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t12 || $[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== t21) {
    t22 = <div className="relative">{t9}{t11}{t12}{t15}{t16}{t17}{t18}{t19}{t20}{t21}</div>;
    $[54] = t12;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  return t22;
};
const filterTabs = ({
  projectsCount,
  contentsCount,
  policyCount,
  reviewsCount,
  benefitsCount
}: {
  projectsCount?: number;
  contentsCount?: number;
  policyCount?: number;
  reviewsCount?: number;
  benefitsCount?: number;
}) => {
  const tabs: ({
    title: string;
    tab: HOMETOWNS_DETAIL_TAB;
  } | null)[] = [{
    title: '답례품',
    tab: HOMETOWNS_DETAIL_TAB.GIFTS
  }, projectsCount !== 0 ? {
    title: '지정기부',
    tab: HOMETOWNS_DETAIL_TAB.PROJECTS
  } : null, contentsCount !== 0 ? {
    title: '콘텐츠',
    tab: HOMETOWNS_DETAIL_TAB.CONTENTS
  } : null, policyCount !== 0 ? {
    title: '정책',
    tab: HOMETOWNS_DETAIL_TAB.POLICY
  } : null, reviewsCount !== 0 ? {
    title: '후기',
    tab: HOMETOWNS_DETAIL_TAB.REVIEWS
  } : null, benefitsCount !== 0 ? {
    title: '혜택',
    tab: HOMETOWNS_DETAIL_TAB.BENEFITS
  } : null];
  return tabs.filter((tab): tab is {
    title: string;
    tab: HOMETOWNS_DETAIL_TAB;
  } => Boolean(tab));
};
function _temp(t0) {
  const {
    context
  } = t0;
  return context?.totalCount ?? 0;
}
function _temp2(t0) {
  const {
    context: context_0
  } = t0;
  return context_0?.totalCount ?? 0;
}
function _temp3(t0) {
  const {
    context: context_1
  } = t0;
  return context_1?.totalCount ?? 0;
}
function _temp4(t0) {
  const {
    context: context_2
  } = t0;
  return context_2?.totalCount ?? 0;
}